import React, { useEffect } from 'react';
import { Form, App, Spin, Button } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { PLAN_OWNER_UPDATE, PLAN_OWNER_DELETE } from '../../../graphql/mutations/planOwners';
import { PLAN_OWNER } from '../../../graphql/queries/planOwners';
import { PlanOwnerForm } from '../../../components/PlanOwner/PlanOwnerForm';
import CardLayout from '../../../layouts/CardLayout';
import { IPlanOwnerPhoto, PlanOwnerFormValues } from '../../../types/planOwner';

const PlanOwnerEdit: React.FC = () => {
  const { message, modal } = App.useApp();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [form] = Form.useForm();

  const { loading: queryLoading, refetch } = useQuery(PLAN_OWNER, {
    variables: { id },
    fetchPolicy: 'network-only',
    onCompleted: data => {
      const planOwner = data.planOwner;
      form.setFieldsValue({
        ...planOwner,
        logo: planOwner.logo ? [{ url: planOwner.logo }] : [],
        photos: planOwner.photos?.map((url: string) => ({ url })) || [],
      });
    },
    onError: error => {
      message.error(error.message);
    },
  });

  const [updatePlanOwner, { loading: updateLoading }] = useMutation(PLAN_OWNER_UPDATE, {
    onCompleted: () => {
      message.success('Plan owner successfully updated');
      navigate('/admin/plan-owners');
    },
    onError: error => {
      message.error(error.message);
    },
  });

  const [deletePlanOwner, { loading: deleteLoading }] = useMutation(PLAN_OWNER_DELETE, {
    onCompleted: () => {
      message.success('Plan owner successfully deleted');
      navigate('/admin/plan-owners');
    },
    onError: error => {
      message.error(error.message);
    },
  });

  useEffect(() => {
    refetch();
  }, [refetch]);

  const onFinish = async (values: PlanOwnerFormValues) => {
    try {
      const photos = values.photos?.map((photo: IPlanOwnerPhoto) => photo.url);
      const logo = (Array.isArray(values.logo) && values.logo[0]?.url) || '';

      await updatePlanOwner({
        variables: {
          id,
          ...values,
          photos,
          logo,
        },
      });
    } catch (error) {
      console.error(error);
    }
  };

  const handleDelete = () => {
    modal.confirm({
      title: 'Delete Plan Owner',
      content: 'Are you sure you want to delete this plan owner?',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk: () => deletePlanOwner({ variables: { id } }),
    });
  };

  if (queryLoading) {
    return <Spin size="large" />;
  }

  return (
    <CardLayout>
      <PlanOwnerForm
        form={form}
        onFinish={onFinish}
        loading={updateLoading}
        submitText="Update Plan Owner"
      />
      <Button danger onClick={handleDelete} loading={deleteLoading}>
        Delete Plan Owner
      </Button>
    </CardLayout>
  );
};

export default PlanOwnerEdit;
